import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BookletCard from "./BookletCard";
import { useDispatch, useSelector } from "react-redux";
import { getServices } from "../../slice/serviceSlice";
import { getMediaUrl } from "../../common/mediaUrl";

const BookletSection = () => {
  const { t, i18n } = useTranslation();
  const trackRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);
  const dispatch = useDispatch();

  const { data: services, loading } = useSelector((state) => state.service);

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch, i18n.language]);

  /* AUTO SCROLL */
  useEffect(() => {
    const track = trackRef.current;
    if (!track || services?.length === 0) return;

    let scrollInterval;

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (
          track.scrollLeft + track.clientWidth >=
          track.scrollWidth - 10
        ) {
          track.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          track.scrollBy({ left: 300, behavior: "smooth" });
        }
      }, 3000);
    };

    startAutoScroll();

    return () => clearInterval(scrollInterval);
  }, [services]);

  /* DOT SYNC */
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !services || services.length === 0) return;

    const index = Math.round(
      track.scrollLeft / track.clientWidth
    );
    setActiveDot(index % services.length);
  };

  if (loading) {
    return <div className="py-12 text-center text-orange-main">{t('booklet_loading')}</div>;
  }

  if (!services || services.length === 0) {
    return null;
  }

  return (
    <section className="py-12 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* HEADING */}
        <h2
          className="
            text-2xl sm:text-3xl md:text-4xl
            text-center font-bold
            text-orange-main
            mb-3 sm:mb-4
            leading-tight
          "
        >
          {t('booklet_heading')}
        </h2>
        <p className="text-center text-gray-500 text-sm md:text-base font-medium mb-10 sm:mb-14 max-w-2xl mx-auto">
          {t('booklet_subheading', 'Temple maps, Panna safari timings and taxi charge guides for your trip.')}
        </p>

        {/* SLIDER */}
        <div className="relative">
          {/* TRACK */}
          <div
            ref={trackRef}
            onScroll={handleScroll}
            className="
              flex gap-6 pb-6
              overflow-x-auto no-scrollbar
              snap-x snap-mandatory
            "
          >
            {services.map((item) => {
              const imgUrl = getMediaUrl(item.image);
              const pdfUrl = item.pdfFile ? getMediaUrl(item.pdfFile) : '#';

              return (
                <div
                  key={item.id}
                  className="
                    min-w-[280px]
                    sm:min-w-[calc(50%-12px)]
                    lg:min-w-[calc(25%-18px)]
                    snap-start
                  "
                >
                  <BookletCard
                    title={t(item.name)}
                    image={imgUrl}
                    link={pdfUrl}
                  />
                </div>
              );
            })}
          </div>

          {/* PAGINATION DOTS (MOBILE ONLY) */}
          <div className="flex justify-center gap-2 mt-6 sm:hidden">
            {services.map((_, i) => (
              <span
                key={i}
                className={`
                  w-2.5 h-2.5 rounded-full transition
                  ${activeDot === i
                    ? "bg-orange-main scale-110"
                    : "bg-orange-light"
                  }
                `}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookletSection;
